import type { GetUserAnalyticsRawDataOutput } from "@/lib/ai/tools/get-user-analytics-raw-data/server";
import {
  buildDataset,
  buildFields,
  extractArray,
  isRecord,
  toDatasetRows,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import { resolveDefaultMetricAggregation } from "@/lib/ai/visualization/data/adapters/metric-aggregation";
import {
  type RawCategoryField,
  readFirstRawNumber,
  readFirstRawText,
  resolveRawCategoryKey,
  sortRowsByPreferredMetric,
  toRawText,
} from "@/lib/ai/visualization/data/adapters/raw-mapping";
import type {
  VizDataset,
  VizDatasetRow,
  VizMetricAggregation,
} from "@/lib/ai/visualization/data/schema";

type MetricUnit = "count" | "duration" | "percent" | "rank";

function toSourceMedium(item: Record<string, unknown>): string | null {
  const source = readFirstRawText(item, [["sourceMedium", "source"]]);
  const medium = readFirstRawText(item, [["sourceMedium", "medium"]]);
  if (source === null && medium === null) return null;
  return `${source ?? "(direct)"} / ${medium ?? "(none)"}`;
}

const CATEGORY_FIELDS: readonly RawCategoryField[] = [
  { key: "date", read: (item) => readFirstRawText(item, ["date"]) },
  { key: "week", read: (item) => readFirstRawText(item, ["week"]) },
  { key: "month", read: (item) => readFirstRawText(item, ["month"]) },
  {
    key: "defaultChannelGroup",
    read: (item) => readFirstRawText(item, ["defaultChannelGroup"]),
  },
  { key: "sourceMedium", read: (item) => toSourceMedium(item) },
  { key: "campaign", read: (item) => readFirstRawText(item, ["campaign"]) },
  { key: "term", read: (item) => readFirstRawText(item, ["term"]) },
  { key: "content", read: (item) => readFirstRawText(item, ["content"]) },
  { key: "country", read: (item) => readFirstRawText(item, ["country"]) },
  { key: "region", read: (item) => readFirstRawText(item, ["region"]) },
  { key: "city", read: (item) => readFirstRawText(item, ["city"]) },
  {
    key: "firstPageLocationURLGroup",
    read: (item) =>
      readFirstRawText(item, [
        ["firstPageLocationURLGroup", "name"],
        ["firstPageLocationURLGroup", "id"],
      ]),
  },
  {
    key: "secondPageLocationURLGroup",
    read: (item) =>
      readFirstRawText(item, [
        ["secondPageLocationURLGroup", "name"],
        ["secondPageLocationURLGroup", "id"],
      ]),
  },
  {
    key: "firstPageTitle",
    read: (item) => readFirstRawText(item, ["firstPageTitle"]),
  },
  {
    key: "secondPageTitle",
    read: (item) => readFirstRawText(item, ["secondPageTitle"]),
  },
  {
    key: "firstPageLocation",
    read: (item) => readFirstRawText(item, ["firstPageLocation"]),
  },
  {
    key: "secondPageLocation",
    read: (item) => readFirstRawText(item, ["secondPageLocation"]),
  },
  {
    key: "device",
    read: (item) =>
      readFirstRawText(item, [
        ["device", "category"],
        ["device", "name"],
        ["device", "id"],
        "device",
      ]),
  },
  { key: "os", read: (item) => readFirstRawText(item, ["os"]) },
  { key: "browser", read: (item) => readFirstRawText(item, ["browser"]) },
  {
    key: "expVariantString",
    read: (item) => readFirstRawText(item, ["expVariantString"]),
  },
] as const;

const TIME_CATEGORY_KEYS = ["date", "week", "month"] as const;

const METRIC_KEYS = [
  "sessionCount",
  "uniqueUserCount",
  "firstSessionRate",
  "conversionCount",
  "conversionRate",
  "surfCount",
  "surfRate",
  "avgSessionDuration",
  "engagementCount",
  "engagementRate",
  "avgPageLocationCount",
  "pageViewEventCount",
  "comparedSessionCount",
  "comparedUniqueUserCount",
  "comparedFirstSessionRate",
  "comparedConversionCount",
  "comparedConversionRate",
  "comparedSurfCount",
  "comparedSurfRate",
  "comparedAvgSessionDuration",
  "comparedEngagementCount",
  "comparedEngagementRate",
  "comparedAvgPageLocationCount",
  "comparedPageViewEventCount",
  "sessionCountDifference",
  "uniqueUserCountDifference",
  "conversionRateDifference",
  "conversionCountDifference",
  "surfRateDifference",
  "surfCountDifference",
  "avgSessionDurationDifference",
  "engagementRateDifference",
  "engagementCountDifference",
  "avgPageLocationCountDifference",
  "pageViewEventCountDifference",
] as const;

type UaRawMetricKey = (typeof METRIC_KEYS)[number];

/**
 * 任意文字列を UA raw metric key へ安全に絞り込む。
 *
 * @remarks
 * 外部入力由来の preferred key を union 型へ落とし込むためのガード。
 */
function isUaRawMetricKey(value: string): value is UaRawMetricKey {
  return (METRIC_KEYS as readonly string[]).includes(value);
}

function hasAnyMetricValue(
  row: VizDatasetRow,
  metricKeys: readonly string[],
): boolean {
  return metricKeys.some((key) => typeof row[key] === "number");
}

const UA_RAW_PERCENT_METRIC_KEYS = new Set<UaRawMetricKey>([
  "firstSessionRate",
  "conversionRate",
  "surfRate",
  "engagementRate",
  "comparedFirstSessionRate",
  "comparedConversionRate",
  "comparedSurfRate",
  "comparedEngagementRate",
  "conversionRateDifference",
  "surfRateDifference",
  "engagementRateDifference",
]);

const UA_RAW_DURATION_METRIC_KEYS = new Set<UaRawMetricKey>([
  "avgSessionDuration",
  "comparedAvgSessionDuration",
  "avgSessionDurationDifference",
]);

const UA_RAW_RANK_METRIC_KEYS = new Set<UaRawMetricKey>([]);

/**
 * UA raw metric key から unit を決定する。
 *
 * @remarks
 * 文字列部分一致は使わず、契約化した metric key のみで unit を確定する。
 */
function inferMetricUnit(metricKey: UaRawMetricKey): MetricUnit {
  if (UA_RAW_PERCENT_METRIC_KEYS.has(metricKey)) return "percent";
  if (UA_RAW_DURATION_METRIC_KEYS.has(metricKey)) return "duration";
  if (UA_RAW_RANK_METRIC_KEYS.has(metricKey)) return "rank";
  return "count";
}

const RAW_AGGREGATION_BY_METRIC_KEY: Record<string, VizMetricAggregation> = {
  conversionRate: {
    kind: "ratio",
    numeratorKey: "conversionCount",
    denominatorKey: "sessionCount",
  },
  surfRate: {
    kind: "ratio",
    numeratorKey: "surfCount",
    denominatorKey: "sessionCount",
  },
  engagementRate: {
    kind: "ratio",
    numeratorKey: "engagementCount",
    denominatorKey: "sessionCount",
  },
  comparedConversionRate: {
    kind: "ratio",
    numeratorKey: "comparedConversionCount",
    denominatorKey: "comparedSessionCount",
  },
  comparedSurfRate: {
    kind: "ratio",
    numeratorKey: "comparedSurfCount",
    denominatorKey: "comparedSessionCount",
  },
  comparedEngagementRate: {
    kind: "ratio",
    numeratorKey: "comparedEngagementCount",
    denominatorKey: "comparedSessionCount",
  },
  avgPageLocationCount: {
    kind: "non-aggregatable",
    reason: "other",
  },
  comparedAvgPageLocationCount: {
    kind: "non-aggregatable",
    reason: "other",
  },
  avgPageLocationCountDifference: {
    kind: "non-aggregatable",
    reason: "other",
  },
  uniqueUserCount: {
    kind: "non-aggregatable",
    reason: "other",
  },
  comparedUniqueUserCount: {
    kind: "non-aggregatable",
    reason: "other",
  },
  uniqueUserCountDifference: {
    kind: "non-aggregatable",
    reason: "other",
  },
};

function resolveRawMetricAggregation(
  metricKey: string,
): VizMetricAggregation | undefined {
  return RAW_AGGREGATION_BY_METRIC_KEY[metricKey];
}

function resolveEffectiveRawMetricAggregation(
  metricKey: UaRawMetricKey,
): VizMetricAggregation {
  return (
    resolveRawMetricAggregation(metricKey) ??
    resolveDefaultMetricAggregation({ unit: inferMetricUnit(metricKey) })
  );
}

function snakeToCamel(value: string): string {
  return value
    .toLowerCase()
    .split("_")
    .map((segment, index) =>
      index === 0
        ? segment
        : `${segment.slice(0, 1).toUpperCase()}${segment.slice(1)}`,
    )
    .join("");
}

/**
 * context.groupBy に指定された既存の UA dimension key を adapter の field key へ解決する。
 *
 * @remarks
 * GraphQL enum と camelCase key の大文字小文字・区切りだけを正規化するため、
 * URL のような略語を含む既存 dimension も個別の変換表なしで保持できる。
 */
function resolveRequestedCategoryKeys(rawGroupBy: unknown): string[] {
  if (!Array.isArray(rawGroupBy)) return [];

  const categoryByNormalizedKey = new Map(
    CATEGORY_FIELDS.map((category) => [
      category.key.replaceAll("_", "").toLowerCase(),
      category.key,
    ]),
  );
  const seen = new Set<string>();

  return rawGroupBy.flatMap((rawKey) => {
    const normalizedKey = toRawText(rawKey)?.replaceAll("_", "").toLowerCase();
    const categoryKey = normalizedKey
      ? categoryByNormalizedKey.get(normalizedKey)
      : null;
    if (!categoryKey || seen.has(categoryKey)) return [];
    seen.add(categoryKey);
    return [categoryKey];
  });
}

function isTimeCategoryKey(
  key: string,
): key is (typeof TIME_CATEGORY_KEYS)[number] {
  return TIME_CATEGORY_KEYS.includes(
    key as (typeof TIME_CATEGORY_KEYS)[number],
  );
}

function isRealizedCategoryValue(value: unknown): boolean {
  if (typeof value === "string") return value.trim().length > 0;
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * category候補が実際の軸値を1件以上持つか判定する。
 *
 * @remarks
 * 空白だけの文字列は表示可能なlabelにならず、非有限numberも軸値として描画できない。
 * `false`の場合、callerはrowをtable用に保持しつつcategory依存viewを公開しない。
 */
function hasRealizedCategoryValue(
  rows: readonly VizDatasetRow[],
  key: string,
): boolean {
  return rows.some((row) => isRealizedCategoryValue(row[key]));
}

function hasCoRealizedCategoryValues(
  rows: readonly VizDatasetRow[],
  firstKey: string,
  secondKey: string,
): boolean {
  return rows.some(
    (row) =>
      isRealizedCategoryValue(row[firstKey]) &&
      isRealizedCategoryValue(row[secondKey]),
  );
}

/** ordinary time-series rendererが集約しないtime cellの一意性を確認する。 */
function hasUniqueRealizedCategoryCells(
  rows: readonly VizDatasetRow[],
  key: string,
): boolean {
  const realizedValues = new Set<string>();
  for (const row of rows) {
    if (!isRealizedCategoryValue(row[key])) continue;
    const value = String(row[key]);
    if (realizedValues.has(value)) return false;
    realizedValues.add(value);
  }
  return realizedValues.size > 0;
}

function isFiniteMetricValue(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * 実現済みcategory cellで安全に投影できるmetricが1つ以上あるか判定する。
 * 一意cellは直接値を保持できるため`some`でよいが、重複cellのratioは寄与する
 * すべてのcellでnumerator/denominatorが揃い、rendererのzero-denominator policyで
 * nullにならない限り候補へ公開しない。
 */
function hasProjectableMetricCells(args: {
  rows: readonly VizDatasetRow[];
  categoryKeys: readonly string[];
  metricKeys: readonly UaRawMetricKey[];
}): boolean {
  const rowsByCell = new Map<string, VizDatasetRow[]>();

  for (const row of args.rows) {
    const categoryValues = args.categoryKeys.map((key) => row[key]);
    if (!categoryValues.every(isRealizedCategoryValue)) continue;

    const cellKey = JSON.stringify(categoryValues.map(String));
    const cellRows = rowsByCell.get(cellKey) ?? [];
    cellRows.push(row);
    rowsByCell.set(cellKey, cellRows);
  }

  const cells = [...rowsByCell.values()];
  if (cells.length === 0) return false;

  const requiresAggregation = cells.some((cellRows) => cellRows.length > 1);
  return args.metricKeys.some((metricKey) => {
    if (!requiresAggregation) {
      return cells.some((cellRows) =>
        cellRows.some((row) => isFiniteMetricValue(row[metricKey])),
      );
    }

    const aggregation = resolveEffectiveRawMetricAggregation(metricKey);
    if (aggregation.kind === "non-aggregatable") return false;
    if (aggregation.kind === "additive") {
      return cells.some((cellRows) =>
        cellRows.some((row) => isFiniteMetricValue(row[metricKey])),
      );
    }

    const cellsWithRatioInput = cells.filter((cellRows) =>
      cellRows.some(
        (row) =>
          isFiniteMetricValue(row[metricKey]) ||
          isFiniteMetricValue(row[aggregation.numeratorKey]) ||
          isFiniteMetricValue(row[aggregation.denominatorKey]),
      ),
    );
    return (
      cellsWithRatioInput.length > 0 &&
      cellsWithRatioInput.every((cellRows) => {
        const hasNumerator = cellRows.some((row) =>
          isFiniteMetricValue(row[aggregation.numeratorKey]),
        );
        const denominatorValues = cellRows.flatMap((row) => {
          const value = row[aggregation.denominatorKey];
          return isFiniteMetricValue(value) ? [value] : [];
        });
        if (!hasNumerator || denominatorValues.length === 0) return false;

        const denominatorTotal = denominatorValues.reduce(
          (total, value) => total + value,
          0,
        );
        return (
          denominatorTotal !== 0 ||
          (aggregation.zeroDenominatorValue !== undefined &&
            aggregation.zeroDenominatorValue !== null)
        );
      })
    );
  });
}

/**
 * 要求されたtime categoryをcanonical順に評価し、grouped projectionを構成できる軸を優先する。
 * grouped viewを構成できない場合も、最初に実現したtime axisへfallbackして通常の
 * time-series/table datasetを維持する。
 */
function resolveTimeCategoryKey(
  categoryKeys: readonly string[],
  rows: readonly VizDatasetRow[],
  metricKeys: readonly UaRawMetricKey[],
): string | null {
  const realizedTimeCategoryKeys = TIME_CATEGORY_KEYS.filter(
    (key) => categoryKeys.includes(key) && hasRealizedCategoryValue(rows, key),
  );
  return (
    realizedTimeCategoryKeys.find(
      (timeCategoryKey) =>
        resolveProjectableNonTimeCategoryKey(
          categoryKeys,
          rows,
          timeCategoryKey,
          metricKeys,
        ) !== null,
    ) ??
    realizedTimeCategoryKeys[0] ??
    null
  );
}

/**
 * 要求順を保ったまま、grouped time-seriesのseries軸として安全なdimensionを選ぶ。
 *
 * @remarks
 * time軸とのrow-level共存だけでは、選択metricをgroup cellへ安全に集約できるとは
 * 限らないため、projection contractも同時に確認する。該当軸がなければ`null`を返し、
 * callerがgrouped viewをadvertiseしないようにする。
 */
function resolveProjectableNonTimeCategoryKey(
  categoryKeys: readonly string[],
  rows: readonly VizDatasetRow[],
  timeCategoryKey: string,
  metricKeys: readonly UaRawMetricKey[],
): string | null {
  return (
    categoryKeys.find(
      (key) =>
        !isTimeCategoryKey(key) &&
        hasCoRealizedCategoryValues(rows, timeCategoryKey, key) &&
        canProjectGroupedTimeSeries({
          rows,
          timeCategoryKey,
          groupCategoryKey: key,
          metricKeys,
        }),
    ) ?? null
  );
}

function canProjectGroupedTimeSeries(args: {
  rows: readonly VizDatasetRow[];
  timeCategoryKey: string;
  groupCategoryKey: string;
  metricKeys: readonly UaRawMetricKey[];
}): boolean {
  return hasProjectableMetricCells({
    rows: args.rows,
    categoryKeys: [args.timeCategoryKey, args.groupCategoryKey],
    metricKeys: args.metricKeys,
  });
}

function resolveProjectableRankingCategoryKey(
  categoryKeys: readonly string[],
  rows: readonly VizDatasetRow[],
  metricKeys: readonly UaRawMetricKey[],
): string | null {
  return (
    categoryKeys.find((categoryKey) =>
      hasProjectableMetricCells({
        rows,
        categoryKeys: [categoryKey],
        metricKeys,
      }),
    ) ?? null
  );
}

/**
 * categoryの有無とは独立に、2つのmetricを2観測以上で比較できるか判定する。
 * scatterのseries軸は任意なので、category欠損だけを理由に候補から除外しない。
 * 比較期間のcurrent/previous/differenceは同じ要求metric familyとして数える。
 * 古い比較outputに要求metric metadataがなければ、実現済みfieldをlegacy fallbackとして使う。
 */
function canProjectScatterPlot(
  rows: readonly VizDatasetRow[],
  metricKeys: readonly UaRawMetricKey[],
  requestedMetricFamilyKeys: readonly UaRawMetricKey[] | null,
  isComparing: boolean,
): boolean {
  const allowedFamilies =
    requestedMetricFamilyKeys && requestedMetricFamilyKeys.length > 0
      ? new Set(requestedMetricFamilyKeys)
      : null;
  const metricKeysByFamily = new Map<UaRawMetricKey, UaRawMetricKey[]>();
  for (const metricKey of metricKeys) {
    const familyKey = resolveMetricFamilyKey(metricKey);
    if (allowedFamilies && !allowedFamilies.has(familyKey)) continue;
    const familyMetricKeys = metricKeysByFamily.get(familyKey) ?? [];
    familyMetricKeys.push(metricKey);
    metricKeysByFamily.set(familyKey, familyMetricKeys);
  }

  const metricFamilies = [...metricKeysByFamily.values()];
  if (metricFamilies.length < 2) return false;

  return metricFamilies.some((xKeys, xIndex) =>
    metricFamilies
      .slice(xIndex + 1)
      .some((yKeys) =>
        xKeys.some((xKey) =>
          yKeys.some(
            (yKey) =>
              rows.filter(
                (row) =>
                  isFiniteMetricValue(row[xKey]) &&
                  isFiniteMetricValue(row[yKey]),
              ).length >= 2,
          ),
        ),
      ),
  );
}

/** comparison variantを要求元のmetric familyへ正規化する。 */
function resolveMetricFamilyKey(metricKey: UaRawMetricKey): UaRawMetricKey {
  let candidate: string = metricKey;
  if (candidate.startsWith("compared")) {
    const base = candidate.slice("compared".length);
    candidate = `${base.slice(0, 1).toLowerCase()}${base.slice(1)}`;
  } else if (candidate.endsWith("Difference")) {
    candidate = candidate.slice(0, -"Difference".length);
  }

  return isUaRawMetricKey(candidate) ? candidate : metricKey;
}

/** tool contextの要求metricをscatter判定用のfamily keyへ解決する。 */
function resolveRequestedMetricFamilyKeys(
  rawMetrics: unknown,
): UaRawMetricKey[] | null {
  if (!Array.isArray(rawMetrics)) return null;

  const metricFamilyKeys = new Set<UaRawMetricKey>();
  for (const rawMetric of rawMetrics) {
    const metricKey = resolveOrderMetricKey(rawMetric);
    if (!metricKey || !isUaRawMetricKey(metricKey)) continue;
    metricFamilyKeys.add(resolveMetricFamilyKey(metricKey));
  }
  return [...metricFamilyKeys];
}

/**
 * 選択軸で投影できるdefault metricを、要求されたorder metric、sessionCount、
 * その他のvisible metricの順に選ぶ。
 *
 * @remarks
 * categoryがないdatasetはcell validationを適用できないため表示順だけで選ぶ。
 * 投影可能な候補がなければnullを返し、callerはdefaultsと対応viewを公開しない。
 */
function resolveDefaultMetricKey(args: {
  rows: readonly VizDatasetRow[];
  visibleMetricKeys: readonly UaRawMetricKey[];
  preferredMetricKey: UaRawMetricKey | null;
  categoryKeys: readonly string[];
}): UaRawMetricKey | null {
  const candidateKeys = [
    ...(args.preferredMetricKey &&
    args.visibleMetricKeys.includes(args.preferredMetricKey)
      ? [args.preferredMetricKey]
      : []),
    ...(args.visibleMetricKeys.includes("sessionCount")
      ? (["sessionCount"] as const)
      : []),
    ...args.visibleMetricKeys,
  ].filter(
    (key, index, keys): key is UaRawMetricKey => keys.indexOf(key) === index,
  );

  if (args.categoryKeys.length === 0) return candidateKeys[0] ?? null;
  return (
    candidateKeys.find((metricKey) =>
      hasProjectableMetricCells({
        rows: args.rows,
        categoryKeys: args.categoryKeys,
        metricKeys: [metricKey],
      }),
    ) ?? null
  );
}

function resolveOrderMetricKey(rawField: unknown): string | null {
  const field = toRawText(rawField)?.trim();
  if (!field) return null;

  if (field.includes("_")) return snakeToCamel(field);
  return `${field.slice(0, 1).toLowerCase()}${field.slice(1)}`;
}

function buildProfile(args: {
  isTimeCategory: boolean;
  isComparing: boolean;
  supportsRankingBars: boolean;
  supportsGroupedTimeSeries: boolean;
  supportsScatterPlot: boolean;
  supportsTimeSeries: boolean;
}): NonNullable<VizDataset["profile"]> {
  if (args.isTimeCategory) {
    return {
      datasetFamily: "ua.raw",
      variant: "timeseries",
      compareMode: args.isComparing ? "period" : "none",
      supportedViews: [
        ...(args.supportsTimeSeries
          ? ([
              "time-series-line",
              "time-series-area",
              "time-series-composed",
            ] as const)
          : []),
        ...(args.supportsGroupedTimeSeries
          ? (["time-series-grouped-line"] as const)
          : []),
        ...(args.supportsScatterPlot ? (["scatter-plot"] as const) : []),
        "table",
      ],
    };
  }

  return {
    datasetFamily: "ua.raw",
    variant: "ranking",
    compareMode: args.isComparing ? "period" : "none",
    supportedViews: [
      ...(args.supportsRankingBars
        ? (["bar-comparison", "bar-ranking"] as const)
        : []),
      ...(args.supportsScatterPlot ? (["scatter-plot"] as const) : []),
      "table",
    ],
  };
}

/**
 * UA raw tool 出力を可視化 dataset へ変換する。
 *
 * @remarks
 * 行変換は `item -> row` の単純マッピングに固定し、行単位で数値が全欠損の場合のみ除外する。
 */
export function buildUaRawDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  if (!isRecord(rawOutput)) return [];
  const output = rawOutput as Partial<GetUserAnalyticsRawDataOutput> &
    Record<string, unknown>;
  const items = extractArray(output, "items");
  if (items.length === 0) return [];

  const context = isRecord(output.context) ? output.context : null;
  const requestedCategoryKeys = resolveRequestedCategoryKeys(context?.groupBy);
  const categoryKey =
    requestedCategoryKeys[0] ??
    resolveRawCategoryKey({
      items,
      categories: CATEGORY_FIELDS,
    });
  const rowCategoryKeys =
    requestedCategoryKeys.length > 0 ? requestedCategoryKeys : [categoryKey];
  const isComparing = context?.isComparing === true;
  const requestedMetricFamilyKeys = resolveRequestedMetricFamilyKeys(
    context?.requestedMetrics,
  );
  const orderBy = isRecord(context?.orderBy) ? context.orderBy : null;
  const preferredMetricKey = resolveOrderMetricKey(
    isRecord(orderBy) ? orderBy.field : null,
  );
  const orderByDirection =
    (isRecord(orderBy) ? toRawText(orderBy.direction) : null) ?? null;

  const rows = toDatasetRows(items, (item) => {
    const row: VizDatasetRow = {};

    for (const requestedCategoryKey of rowCategoryKeys) {
      const category = CATEGORY_FIELDS.find(
        ({ key }) => key === requestedCategoryKey,
      );
      row[requestedCategoryKey] = category?.read(item) ?? null;
    }

    for (const metricKey of METRIC_KEYS) {
      const metricValue = readFirstRawNumber(item, [metricKey]);
      if (metricValue !== null) row[metricKey] = metricValue;
    }

    return hasAnyMetricValue(row, METRIC_KEYS) ? row : null;
  });
  if (rows.length === 0) return [];

  const sortedRows = sortRowsByPreferredMetric({
    rows,
    categoryKey,
    preferredMetricKey,
    orderByDirection,
  });

  const visibleMetricKeys = METRIC_KEYS.filter((key) =>
    sortedRows.some((row) => typeof row[key] === "number"),
  );
  const requestedMetricFamilyKeySet = new Set(requestedMetricFamilyKeys ?? []);
  const constrainsChartMetrics =
    isComparing &&
    requestedMetricFamilyKeys !== null &&
    requestedMetricFamilyKeySet.size > 0;
  const chartMetricKeys = constrainsChartMetrics
    ? visibleMetricKeys.filter((metricKey) =>
        requestedMetricFamilyKeySet.has(resolveMetricFamilyKey(metricKey)),
      )
    : visibleMetricKeys;
  const normalizedPreferredMetricKey =
    preferredMetricKey && isUaRawMetricKey(preferredMetricKey)
      ? preferredMetricKey
      : null;
  const timeCategoryKey = resolveTimeCategoryKey(
    rowCategoryKeys,
    sortedRows,
    chartMetricKeys,
  );
  const groupedSeriesKey = timeCategoryKey
    ? resolveProjectableNonTimeCategoryKey(
        rowCategoryKeys,
        sortedRows,
        timeCategoryKey,
        chartMetricKeys,
      )
    : null;
  const isTimeCategory = timeCategoryKey !== null;
  const projectableRankingCategoryKey = isTimeCategory
    ? null
    : resolveProjectableRankingCategoryKey(
        rowCategoryKeys,
        sortedRows,
        chartMetricKeys,
      );
  const defaultCategoryKey = isTimeCategory
    ? (groupedSeriesKey ?? timeCategoryKey)
    : projectableRankingCategoryKey;
  const projectableTimeMetricKey =
    timeCategoryKey &&
    hasUniqueRealizedCategoryCells(sortedRows, timeCategoryKey)
      ? resolveDefaultMetricKey({
          rows: sortedRows,
          visibleMetricKeys: chartMetricKeys,
          preferredMetricKey: normalizedPreferredMetricKey,
          categoryKeys: [timeCategoryKey],
        })
      : null;
  const projectableGroupedMetricKey =
    timeCategoryKey && groupedSeriesKey
      ? resolveDefaultMetricKey({
          rows: sortedRows,
          visibleMetricKeys: chartMetricKeys,
          preferredMetricKey: normalizedPreferredMetricKey,
          categoryKeys: [timeCategoryKey, groupedSeriesKey],
        })
      : null;
  const defaultValueKey = isTimeCategory
    ? (projectableTimeMetricKey ?? projectableGroupedMetricKey)
    : projectableRankingCategoryKey
      ? resolveDefaultMetricKey({
          rows: sortedRows,
          visibleMetricKeys: chartMetricKeys,
          preferredMetricKey: normalizedPreferredMetricKey,
          categoryKeys: [projectableRankingCategoryKey],
        })
      : null;
  const realizedCategoryKeys = new Set(
    rowCategoryKeys.filter((key) => hasRealizedCategoryValue(sortedRows, key)),
  );

  const fields = buildFields([
    ...rowCategoryKeys.map((key) => {
      const isRealized = realizedCategoryKeys.has(key);
      return {
        key,
        role:
          key === timeCategoryKey
            ? ("x" as const)
            : key === groupedSeriesKey
              ? ("series" as const)
              : ("dim" as const),
        type: isTimeCategoryKey(key) ? ("date" as const) : ("string" as const),
        ...(!isRealized
          ? {
              internal: true,
              defaultVisible: false,
              exportable: false,
            }
          : null),
        ...(key === "defaultChannelGroup"
          ? { labelDomain: "ua.defaultChannelGroup" as const }
          : null),
      };
    }),
    ...visibleMetricKeys.map((metricKey) => ({
      key: metricKey,
      role: "metric" as const,
      type: "number" as const,
      unit: inferMetricUnit(metricKey),
      metricFamily: resolveMetricFamilyKey(metricKey),
      ...(resolveRawMetricAggregation(metricKey)
        ? { aggregation: resolveRawMetricAggregation(metricKey) }
        : null),
      ...(constrainsChartMetrics &&
      !requestedMetricFamilyKeySet.has(resolveMetricFamilyKey(metricKey))
        ? {
            internal: true,
            defaultVisible: false,
            exportable: false,
          }
        : null),
    })),
  ]);

  return [
    buildDataset({
      id: isTimeCategory ? "ua-raw-timeseries" : "ua-raw-ranking",
      profile: buildProfile({
        isTimeCategory,
        isComparing,
        supportsRankingBars: projectableRankingCategoryKey !== null,
        supportsGroupedTimeSeries:
          !isComparing &&
          groupedSeriesKey !== null &&
          projectableGroupedMetricKey !== null,
        supportsScatterPlot: canProjectScatterPlot(
          sortedRows,
          chartMetricKeys,
          requestedMetricFamilyKeys,
          isComparing,
        ),
        supportsTimeSeries: projectableTimeMetricKey !== null,
      }),
      fields,
      rows: sortedRows,
      defaults: {
        ...(defaultCategoryKey ? { category: defaultCategoryKey } : null),
        ...(defaultValueKey ? { value: defaultValueKey } : null),
        ...(timeCategoryKey && defaultValueKey
          ? { x: timeCategoryKey, y: [defaultValueKey] }
          : null),
        ...(groupedSeriesKey ? { series: groupedSeriesKey } : null),
      },
      source,
    }),
  ];
}
